import { Inject, Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TypedAction } from "@ngrx/store/src/models";
import { CDS_RES_HEADER } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouterState, RouteSelector } from "@obg/ngx.router";
import { Request, Response } from "express";
import { filter, tap } from "rxjs/operators";

import { REQUEST_TOKEN, RESPONSE_TOKEN } from "../model";

import { CacheControlConfig } from "./cache-control.config";
import { RESPONSE_TYPE_HEADER_NAME, ResponseTypeHeaderValue } from "./cache-control.const";

@Injectable()
export class ServerCacheControlEffect {
	navigationEnd$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RouteAction.navigationEnd),
				filter(() => this.isEnabled),
				concatLatestFrom(() => this.store.select(RouteSelector.getCurrent)),
				filter(([, currentRoute]) => !!currentRoute),
				tap(([, { name }]: [TypedAction<typeof RouteAction.navigationEnd.type>, Route]) => {
					if (this.response.headersSent) {
						return;
					}
					let maxAgeTimeout = this.config.getMaxAge(name);
					if (maxAgeTimeout < 0) {
						this.logger.info(`Remove Cache-Control headers for route [${name}]`);
						this.response.removeHeader("Cache-Control");
						this.response.removeHeader("Vary");
						return;
					}

					const isBot = this.request.headers[this.config.getBotHeader()];
					if (isBot) {
						maxAgeTimeout = this.config.getBotMaxAge();
					}

					const isCdsEnabled = this.config.getCdsByRoute(name);

					const responseTypeHeader = isBot ? ResponseTypeHeaderValue.SeoSsr : ResponseTypeHeaderValue.Ssr;
					this.response.header(RESPONSE_TYPE_HEADER_NAME, responseTypeHeader);

					this.logger.info(
						`setting Cache-Control headers for route [${name}] with max age of [${maxAgeTimeout}] seconds, cdsEnabled: ${isCdsEnabled}`
					);
					this.response.header("Cache-Control", `public, max-age=${maxAgeTimeout}`);

					if (isCdsEnabled) {
						this.response.header(CDS_RES_HEADER, "true");
					}

					const varyHeaders = this.config.getVaryHeader(name);
					this.logger.info(`setting Vary header for route [${name}] with value ${varyHeaders}`);
					this.response.header("Vary", varyHeaders);
				})
			),
		{ dispatch: false }
	);

	private logger: Log;
	private readonly isEnabled: boolean;

	constructor(
		logging: LoggingFactory,
		private actions$: Actions,
		private store: Store<RouterState>,
		private config: CacheControlConfig,
		@Inject(REQUEST_TOKEN) private request: Request,
		@Inject(RESPONSE_TOKEN) private response: Response
	) {
		this.logger = logging.get("Cache Control Effect");
		this.isEnabled = this.config.getIsEnabled();
		if (!this.isEnabled) {
			this.logger.info("cache control has been disabled from config");
			return;
		}

		this.logger.info("starting cache control effects");
	}
}
